/**
 * What a conversation is attached to — `docs/activity.md` §7.8 (Board 8 D).
 *
 * The chips with ×, *Add a contact, property or job…* (§9.2's one search),
 * and *Suggested* — the open jobs at the attached properties and the
 * contact's other properties, not yet attached, at most three — with **Add**.
 * Every change is saved at once (`PUT /conversations/:id/links`); old
 * messages stay where they were.
 */
import { useEffect, useMemo, useState } from 'react'
import type { ConversationThread, ThreadLink } from '@rowted/shared'
import { Popover } from '../../components/overlays.js'
import { activityRequest } from '../api.js'
import { Glyph, type GlyphName } from '../glyphs.js'
import { chipLabel, errorWords } from './look.js'

interface AttachItem { type: 'job' | 'contact' | 'property'; id: string; label: string; sub: string }
interface AttachAnswer { jobs: AttachItem[]; contacts: AttachItem[]; properties: AttachItem[] }

const GLYPH: Record<string, GlyphName> = { job: 'layers', property: 'pin', contact: 'user' }
const keyOf = (link: { type: string; id: string }): string => `${link.type}:${link.id}`

function AddRow({ item, onAdd }: { item: { type: string; label: string; sub?: string }; onAdd: () => void }) {
  return (
    <button type="button" onClick={onAdd}
      className="flex w-full items-center gap-2 rounded-md px-1 py-1.5 text-left text-[12.5px] hover:bg-surface-alt">
      <span className="inline-flex text-text-muted"><Glyph name={GLYPH[item.type] ?? 'link'} size={13} /></span>
      <span className="flex min-w-0 flex-col">
        <span className="truncate">{item.label}</span>
        {item.sub && <span className="truncate text-[11px] text-text-muted">{item.sub}</span>}
      </span>
      <span className="flex-1" />
      <span className="font-semibold text-accent">Add</span>
    </button>
  )
}

export function LinksEditor({
  thread, anchor, onClose, onSaved,
}: { thread: ConversationThread; anchor: HTMLElement | null; onClose: () => void; onSaved: () => void }) {
  const [links, setLinks] = useState<ThreadLink[]>(thread.links)
  const [q, setQ] = useState('')
  const [found, setFound] = useState<AttachAnswer | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => { setLinks(thread.links) }, [thread.links])

  useEffect(() => {
    const term = q.trim()
    if (!term) { setFound(null); return }
    let live = true
    const timer = window.setTimeout(() => {
      activityRequest<AttachAnswer>(`/activity/attach?q=${encodeURIComponent(term)}`)
        .then((answer) => { if (live) setFound(answer) })
        .catch(() => { if (live) setFound({ jobs: [], contacts: [], properties: [] }) })
    }, 200)
    return () => { live = false; window.clearTimeout(timer) }
  }, [q])

  const attached = useMemo(() => new Set(links.map(keyOf)), [links])
  const suggested = thread.suggested.filter((link) => !attached.has(keyOf(link)))

  const save = async (next: ThreadLink[]) => {
    const before = links
    setLinks(next)
    setError(null)
    try {
      await activityRequest(`/conversations/${thread.id}/links`, {
        method: 'PUT', body: { links: next.map((link) => ({ type: link.type, id: link.id })) },
      })
      onSaved()
    } catch (cause) {
      setLinks(before)
      setError(errorWords(cause))
    }
  }

  const add = (item: { type: string; id: string; label: string }) => {
    if (attached.has(keyOf(item))) return
    setQ('')
    void save([...links, { type: item.type as ThreadLink['type'], id: item.id, label: item.label, auto: false }])
  }

  const results = found ? [...found.jobs, ...found.contacts, ...found.properties].filter((item) => !attached.has(keyOf(item))) : []

  return (
    /* Board 8 D: 380 inside 12 px of padding and a hairline — 406 across; ink, not the line's grey. */
    <Popover open onClose={onClose} anchor={anchor} width={406} label="Attached to" className="!z-[70] !rounded-xl p-3 text-text">
      <b className="text-[13px]">Attached to</b>
      <div className="mb-2.5 mt-0.5 text-[11.5px] text-text-muted">
        {thread.row.name}’s conversation · each message also shows on these records
      </div>
      {links.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {links.map((link) => (
            <span key={keyOf(link)}
              className="inline-flex h-[30px] max-w-full items-center gap-[5px] rounded-lg border border-accent-line bg-green-band-bg pl-[9px] pr-1.5 text-[12px] font-semibold">
              <span className="inline-flex text-accent"><Glyph name={GLYPH[link.type] ?? 'link'} size={12} /></span>
              <span className="truncate">{chipLabel(link.type, link.label)}</span>
              <button type="button" aria-label={`Remove ${link.label}`} className="inline-flex text-text-muted"
                onClick={() => void save(links.filter((one) => keyOf(one) !== keyOf(link)))}>
                <Glyph name="close" size={11} stroke={2.4} />
              </button>
            </span>
          ))}
        </div>
      )}
      <div className="h-2.5" />
      <label className="flex h-[34px] w-full items-center gap-2.5 rounded-[11px] border-[1.5px] border-line bg-surface px-3.5 focus-within:border-accent">
        <span className="inline-flex text-text-faint"><Glyph name="search" size={18} /></span>
        <input
          autoFocus
          value={q}
          onChange={(event) => setQ(event.target.value)}
          placeholder="Add a contact, property or job…"
          aria-label="Add a contact, property or job"
          className="min-w-0 flex-1 bg-transparent text-[15px] font-medium outline-none placeholder:text-text-faint focus-visible:shadow-none"
        />
      </label>
      {q.trim() && found && (
        <div className="mt-1.5 max-h-[220px] overflow-y-auto">
          {results.length === 0
            ? <div className="px-1 py-2 text-[12.5px] text-text-muted">Nothing matches “{q.trim()}”.</div>
            : results.map((item) => <AddRow key={keyOf(item)} item={item} onAdd={() => add(item)} />)}
        </div>
      )}
      {!q.trim() && suggested.length > 0 && (
        <>
          <div className="mx-0.5 mb-1 mt-2.5 text-[10.5px] font-semibold uppercase tracking-[.06em] text-text-faint">Suggested</div>
          {suggested.map((link) => <AddRow key={keyOf(link)} item={link} onAdd={() => add(link)} />)}
        </>
      )}
      {error && <div className="mt-2 text-[12px] text-danger">{error}</div>}
    </Popover>
  )
}
